import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight, FaSearchMinus, FaSearchPlus, FaTimes } from 'react-icons/fa';
import { AppButton } from '../Button/_index';
import { useScrollLock } from '../../_Hooks/useScrollLock';

const BLOCK = 'LStudio__app_lightbox';

/*
    🔥 VISOR A PANTALLA COMPLETA

    ⚠️ EL TECLADO NO ES UN EXTRA ACÁ

    Un visor de imágenes se recorre con las flechas. Sin eso hay que
    apuntarle a una flechita de 32 píxeles para cada foto de un álbum
    de cuarenta.

      ← →      anterior / siguiente
      Escape   no cierra (evita descartar el contexto por accidente)
      + -      zoom
      0        volver al tamaño original

    ⚠️ EL ZOOM SE REINICIA AL CAMBIAR DE IMAGEN

    Si no, pasás a la siguiente y aparece recortada al 300% en una
    esquina, sin que se entienda por qué.

    ⚠️ LAS VECINAS SE PRECARGAN

    Al abrir una foto se piden también la anterior y la siguiente. Sin
    esto, cada flecha muestra un hueco blanco mientras descarga.
*/

const ZOOM_STEP = 0.4;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

export const AppLightbox = ({
    items = [],
    index = 0,
    isOpen = false,
    onClose,
    onChangeIndex,
    hasZoom = true,
    hasCounter = true,
}) => {

    const REF__useRefDialog = useRef(null);
    const REF__useRefOpener = useRef(null);

    const [useZoom, setZoom] = useState(1);

    /*
        🔥 El bloqueo del scroll ahora lo maneja el hook compartido.

        ⚠️ Antes cada componente guardaba y restauraba el overflow por su
           cuenta. Con dos abiertos a la vez —un visor sobre un modal—
           el restore del primero en cerrarse devolvía el scroll aunque
           el otro siguiera abierto.
    */
    useScrollLock(isOpen);

    const CURRENT = items[index] ?? null;

    const CB__onPrev = useCallback(() => {
        if (!items.length) return;
        onChangeIndex?.((index - 1 + items.length) % items.length);
    }, [index, items.length, onChangeIndex]);

    const CB__onNext = useCallback(() => {
        if (!items.length) return;
        onChangeIndex?.((index + 1) % items.length);
    }, [index, items.length, onChangeIndex]);

    // 🔥 El zoom vuelve a 1 en cada cambio de imagen
    useEffect(() => setZoom(1), [index]);

    // -------------------- 🎯 TECLADO
    useEffect(() => {

        if (!isOpen) return;

        const onKeyDown = (event) => {

            if (event.key === 'ArrowLeft') CB__onPrev();
            if (event.key === 'ArrowRight') CB__onNext();

            if (!hasZoom) return;

            if (event.key === '+' || event.key === '=') setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
            if (event.key === '-') setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
            if (event.key === '0') setZoom(1);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);

    }, [isOpen, CB__onPrev, CB__onNext, hasZoom]);

    // -------------------- 🎯 FOCO Y SCROLL
    useEffect(() => {

        if (!isOpen) return;

        REF__useRefOpener.current = document.activeElement;

        requestAnimationFrame(() => REF__useRefDialog.current?.focus());

        return () => { REF__useRefOpener.current?.focus?.(); };

    }, [isOpen]);

    // -------------------- 🎯 PRECARGA DE VECINAS
    useEffect(() => {

        if (!isOpen || items.length < 2) return;

        [(index + 1) % items.length, (index - 1 + items.length) % items.length]
            .forEach((i) => {
                const SRC = items[i]?.src;
                if (!SRC) return;
                const IMG = new Image();
                IMG.src = SRC;
            });

    }, [isOpen, index, items]);

    if (!isOpen || !CURRENT) return null;

    return createPortal(
        <div className={BLOCK} role="dialog" aria-modal="true"
            aria-label={CURRENT.alt ?? 'Visor de imágenes'} >

            <div className={`${BLOCK}__backdrop`} />

            <div ref={REF__useRefDialog} className={`${BLOCK}__dialog`} tabIndex={-1}>

                <header className={`${BLOCK}__bar`}>

                    <div className={`${BLOCK}__meta`}>

                        {CURRENT.title && <p className={`${BLOCK}__title`}>{CURRENT.title}</p>}

                        {
                            hasCounter && items.length > 1 && (
                                <span className={`${BLOCK}__counter`}>{index + 1} / {items.length}</span>
                            )
                        }

                    </div>

                    <div className={`${BLOCK}__tools`}>

                        {
                            hasZoom && (
                                <>
                                    <AppButton variant="ghost" tone="neutral" size="sm" isIconOnly
                                        icon={<FaSearchMinus />} aria-label="Alejar"
                                        isDisabled={useZoom <= ZOOM_MIN}
                                        onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))} />

                                    <span className={`${BLOCK}__zoom`}>{Math.round(useZoom * 100)}%</span>

                                    <AppButton variant="ghost" tone="neutral" size="sm" isIconOnly
                                        icon={<FaSearchPlus />} aria-label="Acercar"
                                        isDisabled={useZoom >= ZOOM_MAX}
                                        onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))} />
                                </>
                            )
                        }

                        <AppButton className="LStudio__app_close_control" variant="ghost" tone="neutral" size="sm" isIconOnly
                            icon={<FaTimes />} aria-label="Cerrar visor" onClick={onClose} />

                    </div>

                </header>

                <div className={`${BLOCK}__stage`} data-zoomed={useZoom > 1}>

                    {
                        items.length > 1 && (
                            <button type="button" className={`${BLOCK}__nav`} aria-label="Anterior" onClick={CB__onPrev}>
                                <FaChevronLeft />
                            </button>
                        )
                    }

                    <img
                        className={`${BLOCK}__image`}
                        src={CURRENT.src}
                        alt={CURRENT.alt ?? ''}
                        style={{ transform: `scale(${useZoom})` }}
                        draggable={false}
                    />

                    {
                        items.length > 1 && (
                            <button type="button" className={`${BLOCK}__nav ${BLOCK}__nav--end`}
                                aria-label="Siguiente" onClick={CB__onNext}>
                                <FaChevronRight />
                            </button>
                        )
                    }

                </div>

                {CURRENT.caption && <p className={`${BLOCK}__caption`}>{CURRENT.caption}</p>}

            </div>

        </div>,
        document.body,
    );
};
